'use client'

import { makeQueryString } from '@/utils/routes'
import { useSearchParams } from 'next/navigation'
import { useCallback } from 'react'
import { useRouter } from '@/libs/tolgee/navigation'

const LOCALE_REGEX = /[a-z]{2}_[A-Z]{2}/

interface PreviewNavigationOptions {
  scroll?: boolean
}

export const usePreviewNavigation = () => {
  const router = useRouter()
  const searchParams = useSearchParams()

  const currentPreviewMode = searchParams?.get('previewMode')
  const currentPreviewToken = searchParams?.get('previewToken')

  const isInPreviewMode = Boolean(currentPreviewMode && currentPreviewToken)

  const getPathName = (href: string) => {
    if (LOCALE_REGEX.test(href?.split('/')?.[1] ?? '')) {
      const currentLocale = LOCALE_REGEX.exec(href?.split('/')?.[1] ?? '')?.[0]
      if (href === `/${currentLocale}`) {
        return '/'
      }
      return href.replaceAll(`/${currentLocale}`, '')
    }
    return href
  }

  const createPreviewQueryString = useCallback(
    (additionalParams?: Record<string, string | undefined>) => {
      if (!isInPreviewMode) {
        return makeQueryString(additionalParams || {})
      }

      const previewParams = {
        previewMode: currentPreviewMode || undefined,
        previewToken: currentPreviewToken || undefined,
        ...additionalParams,
      }

      return makeQueryString(previewParams)
    },
    [isInPreviewMode, currentPreviewMode, currentPreviewToken]
  )

  const pushWithPreview = useCallback(
    (href: string, options?: PreviewNavigationOptions) => {
      if (!isInPreviewMode) {
        router.push(getPathName(href), options)
        return
      }

      const [pathname, existingQuery] = href.split('?')
      const existingParams = new URLSearchParams(existingQuery || '')

      existingParams.set('previewMode', currentPreviewMode!)
      existingParams.set('previewToken', currentPreviewToken!)

      const newHref = `${pathname}?${existingParams.toString()}`
      router.push(getPathName(newHref), options)
    },
    [isInPreviewMode, currentPreviewMode, currentPreviewToken, router]
  )

  const replaceWithPreview = useCallback(
    (href: string, options?: PreviewNavigationOptions) => {
      if (!isInPreviewMode) {
        router.replace(getPathName(href), options)
        return
      }

      const [pathname, existingQuery] = href.split('?')
      const existingParams = new URLSearchParams(existingQuery || '')

      existingParams.set('previewMode', currentPreviewMode!)
      existingParams.set('previewToken', currentPreviewToken!)

      const newHref = `${pathname}?${existingParams.toString()}`
      router.replace(getPathName(newHref), options)
    },
    [isInPreviewMode, currentPreviewMode, currentPreviewToken, router]
  )

  const createPreviewUrl = useCallback(
    (
      pathname: string,
      additionalParams?: Record<string, string | undefined>
    ) => {
      const queryString = createPreviewQueryString(additionalParams)
      return `${pathname}${queryString}`
    },
    [createPreviewQueryString]
  )

  return {
    // State
    isInPreviewMode,
    currentPreviewMode,
    currentPreviewToken,

    // Methods
    pushWithPreview,
    replaceWithPreview,
    createPreviewUrl,
    createPreviewQueryString,

    // Original router methods (fallback)
    router,
  }
}
